import DataProvider from '@/utils/helpers/DataProvider';
import StoreUtil from '@/utils/helpers/StoreUtil';

import { IVuexState } from '@/models/interfaces/store';
import {
  IActivity,
  IActivityLesson,
  IActivitySolutionResponse,
  ISolutionHistory,
} from '@/models/interfaces/activities';
import { ActionTree, MutationTree, Module } from 'vuex';
import { RootState } from '..';
import findActivityAssetsFromPayload from '@/utils/helpers/activity-assets';

export type PrefetchedActivity = {
  id: string;
  activity: IActivity;
};

export interface State {
  activity: IVuexState<IActivity>;
  prefetchedActivities: PrefetchedActivity[];
  archiveLesson: IVuexState<IActivityLesson[]>;
  currentLesson: IVuexState<IActivityLesson[]>;
  finishedOfflineActivities: string[];
  // Keeps track of Lingos awarded for current activity, as scores are saved
  awardedLingos: number;
  selectedArchiveLesson: string;
  // Current activity score percentage
  currentScorePercentage: number;
}

export const actions: ActionTree<State, RootState> = {
  async fetchActivity(
    { commit },
    {
      activityId,
      language,
      shouldSetPendingState,
    }: {
      activityId: string;
      language: string;
      shouldSetPendingState: boolean;
    },
  ): Promise<IActivity> {
    if (shouldSetPendingState) {
      commit('setActivity');
    }

    try {
      const { data: result } = await DataProvider.get<IActivity>(
        `activities/${activityId}`,
        {
          params: {
            language,
          },
        },
      );
      commit('setActivity', result);
      return result;
    } catch (error) {
      commit('setActivity', error);
      // @ts-ignore
      return error;
    }
  },
  async prefetchActivities({ commit, dispatch, rootState }, ids: string[]) {
    const prefetchedData: PrefetchedActivity[] = [];
    const language = rootState.profile.languages.data?.teachingLanguage || 'en';

    try {
      for (const id of ids) {
        dispatch('fetchActivity', {
          activityId: id,
          language,
          shouldSetPendingState: false,
        }).then((activity: IActivity) => {
          prefetchedData.push({
            id,
            activity,
          });

          const activityAssets = findActivityAssetsFromPayload(activity);
          dispatch('preloadAssets/preloadAssets', activityAssets, {
            root: true,
          });
        });
      }

      commit('setPrefetchedActivities', prefetchedData);
    } catch (error) {
      return error;
    }
  },
  async fetchArchiveLesson({ commit, rootState }): Promise<IActivityLesson[]> {
    const { session } = rootState;
    const { userId } = session;

    commit('setActivityLesson');

    try {
      const { data } = await DataProvider.get<{
        activities: IActivityLesson[];
      }>(`users/${userId}/lessonArchive`);
      commit('setActivityLesson', data.activities);
      return data.activities;
    } catch (error) {
      commit('setActivityLesson', error);
      // @ts-ignore
      return error;
    }
  },
  async fetchCurrentLesson({
    commit,
    rootState,
  }): Promise<{ activities: IActivityLesson[]; unlockLesson: boolean }> {
    const { session } = rootState;
    const { userId } = session;

    commit('setCurrentLesson');

    try {
      const { data } = await DataProvider.get<{
        activities: IActivityLesson[];
        unlockLesson: boolean;
      }>(`users/${userId}/currentLesson`);
      commit('setCurrentLesson', data);
      return data;
    } catch (error) {
      commit('setCurrentLesson', error);
      // @ts-ignore
      return error;
    }
  },
  async sendSolutions(
    { commit, rootState },
    payload: ISolutionHistory[],
  ): Promise<IActivitySolutionResponse> {
    const { session } = rootState;
    const { userId } = session;

    try {
      const result = await DataProvider.post<IActivitySolutionResponse>(
        `users/${userId}/solution`,
        payload,
      );
      commit('addAwardedLingos', result.data.lingoCredit);
      return result.data;
    } catch (error) {
      // @ts-ignore
      return error;
    }
  },
};

export const mutations: MutationTree<State> = {
  setActivity(state, data?: IActivity | Error) {
    state.activity = StoreUtil.updateState(state.activity, data);
  },
  setPrefetchedActivities(state, data: PrefetchedActivity[]) {
    state.prefetchedActivities = data;
  },
  setActivityLesson(state, data?: IActivityLesson[] | Error) {
    state.archiveLesson = StoreUtil.updateState(state.archiveLesson, data);
  },
  setCurrentLesson(state, data?: IActivityLesson[] | Error) {
    state.currentLesson = StoreUtil.updateState(state.currentLesson, data);
  },
  setOfflineFinishedActivity(state, id: string) {
    state.finishedOfflineActivities = [...state.finishedOfflineActivities, id];
  },
  addAwardedLingos(state, lingos: number) {
    state.awardedLingos += lingos;
  },
  resetAwardedLingos(state) {
    state.awardedLingos = 0;
  },
  // Keep track of the selected archive lesson between training page reloads
  setSelectedArchiveLesson(state, selectedLessonTitle: string = '') {
    state.selectedArchiveLesson = selectedLessonTitle;
  },
  setCurrentScorePercentage(state, score: number) {
    state.currentScorePercentage = score;
  },
};

const store: Module<State, RootState> = {
  namespaced: true,
  state: {
    activity: StoreUtil.state<IActivity>(),
    archiveLesson: StoreUtil.state<IActivityLesson[]>(),
    currentLesson: StoreUtil.state<IActivityLesson[]>(),
    prefetchedActivities: [],
    finishedOfflineActivities: [],
    awardedLingos: 0,
    selectedArchiveLesson: '',
    currentScorePercentage: 0,
  },
  mutations,
  actions,
};

export default store;
